import { Component, Injector, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import {
  AbstractComponent,
  Attendance,
  AttendanceService,
  AttendanceStatusEnum,
  Card,
  CardService,
  ChatContextRef,
  ChatContextRefKindEnum,
  Contact,
  ContactService,
  Pager,
} from 'lib-trend-core';
import { Observable, Subject, of } from 'rxjs';
import { catchError, debounceTime, finalize, map, switchMap, takeUntil } from 'rxjs/operators';
import { contextRefIcon } from './chat-context-ref.util';

/** One selectable work record (already reduced to the persisted snapshot). */
interface ChatContextRefOption {
  refId: string;
  label: string;
  subtitle: string;
  /**
   * Attendance status badge — PICKER DISPLAY ONLY (translated at render time),
   * never part of the persisted snapshot, which carries data exclusively.
   */
  statusKey?: string;
}

const RESULT_LIMIT = 10;

/** AttendanceStatusEnum → the app's existing status label keys. */
const ATTENDANCE_STATUS_KEYS: Record<string, string> = {
  PENDING: 'attendance.status.pending',
  IN_PROGRESS: 'attendance.status.inProgress',
  PAUSED: 'attendance.status.paused',
  CLOSED: 'attendance.status.closed',
  OVERTIME: 'attendance.status.overtime',
};

/**
 * Work-reference picker (SPEC-2026-07-internal-chat, T-22 / RF-35 / AC-31).
 *
 * Searches the three referenceable work records through the EXISTING
 * application services — `AttendanceService.getPagerToAttendance`,
 * `CardService` `/list` and `ContactService.getAll` — so every result is
 * already scoped to the caller's company and permissions by those endpoints.
 * Chat adds no data access of its own here.
 *
 * Closing with a selection returns the `contextRef` the composer attaches to
 * the outgoing message: `{ kind, refId, snapshot: { label, subtitle } }`. The
 * snapshot carries DATA only (names, protocol, phone, funnel/stage) — never
 * translated UI text, because it is persisted and read by every member in
 * their own language.
 */
@Component({
  templateUrl: './chat-context-ref-picker-dialog.component.html',
  styleUrls: ['./chat-context-ref-picker-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ChatContextRefPickerDialogComponent extends AbstractComponent implements OnInit {

  readonly kinds = ChatContextRefKindEnum;

  /**
   * Attendance status filter (ATTENDANCE tab only) — forwarded to the pager's
   * `status` query param, so role/department scoping stays server-side.
   * Ordered by attendance flow; empty string = every status.
   */
  readonly statusOptions: Array<{ value: AttendanceStatusEnum; labelKey: string }> = [
    { value: AttendanceStatusEnum.PENDING, labelKey: 'attendance.status.pending' },
    { value: AttendanceStatusEnum.IN_PROGRESS, labelKey: 'attendance.status.inProgress' },
    { value: AttendanceStatusEnum.PAUSED, labelKey: 'attendance.status.paused' },
    { value: AttendanceStatusEnum.OVERTIME, labelKey: 'attendance.status.overtime' },
    { value: AttendanceStatusEnum.CLOSED, labelKey: 'attendance.status.closed' },
  ];

  activeKind: ChatContextRefKindEnum = ChatContextRefKindEnum.ATTENDANCE;
  term = '';
  statusFilter: AttendanceStatusEnum | '' = '';
  options: ChatContextRefOption[] = [];
  hasError = false;

  /**
   * Server pagination state (ATTENDANCE and CONTACT — their endpoints answer a
   * Pager). CARD stays unpaginated: its /list endpoint returns a plain array.
   * `pager` drives the Bootstrap pagination below the list; null hides it.
   */
  page = 1;
  pager: Pager<unknown> | null = null;

  private readonly queries = new Subject<void>();

  constructor(
    injector: Injector,
    private readonly dialogRef: MatDialogRef<ChatContextRefPickerDialogComponent, ChatContextRef | null>,
    private readonly attendanceService: AttendanceService,
    private readonly cardService: CardService,
    private readonly contactService: ContactService,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.queries
      .pipe(
        debounceTime(350),
        switchMap(() => this.loadActiveKind()),
        takeUntil(this.destroy$),
      )
      .subscribe((options) => this.options = options);

    this.search();
  }

  get kindIcon(): string {
    return contextRefIcon(this.activeKind);
  }

  selectKind(kind: ChatContextRefKindEnum): void {
    if (this.activeKind === kind) return;
    this.activeKind = kind;
    this.options = [];
    this.pager = null;
    this.search();
  }

  /** Term/status/tab changed — restart from the first page. */
  search(): void {
    this.page = 1;
    this.trigger();
  }

  /** Total pages of the current server result (at least 1). */
  get pageCount(): number {
    if (!this.pager?.perPage) return 1;
    return Math.max(1, Math.ceil((this.pager.total ?? 0) / this.pager.perPage));
  }

  /** Pagination page change — same query, new page. */
  onPager(newPage: number): void {
    if (newPage < 1 || newPage > this.pageCount || newPage === this.page) return;
    this.page = newPage;
    this.trigger();
  }

  private trigger(): void {
    this.loading = true;
    this.hasError = false;
    this.queries.next();
  }

  /** Confirms the selection — the composer receives the built reference. */
  choose(option: ChatContextRefOption): void {
    const reference: ChatContextRef = {
      kind: this.activeKind,
      refId: option.refId,
      snapshot: option.subtitle ? { label: option.label, subtitle: option.subtitle } : { label: option.label },
    };
    this.dialogRef.close(reference);
  }

  close(): void {
    this.dialogRef.close(null);
  }

  trackOption(_index: number, option: ChatContextRefOption): string {
    return option.refId;
  }

  private loadActiveKind(): Observable<ChatContextRefOption[]> {
    const term = this.term.trim();

    switch (this.activeKind) {
      case ChatContextRefKindEnum.CARD:
        return this.loadCards(term);
      case ChatContextRefKindEnum.CONTACT:
        return this.loadContacts(term);
      default:
        return this.loadAttendances(term);
    }
  }

  // Attendances arrive from the pager already scoped by role/department and
  // filtered to `removed: $ne true` (AGG_FIND_ATTENDANCES) — the client-side
  // filter below is a belt-and-suspenders guard, not the rule. The row shows
  // contact + #protocol · phone, with the status as a translated badge
  // (display only — the persisted snapshot carries data, never UI text).
  private loadAttendances(term: string): Observable<ChatContextRefOption[]> {
    const params = this.statusFilter ? { status: this.statusFilter } : {};
    return this.attendanceService.getPagerToAttendance(this.page, RESULT_LIMIT, term, params)
      .pipe(
        map((pager: Pager<Attendance>) => {
          this.pager = pager ?? null;
          return (pager?.list ?? [])
            .filter((attendance) => attendance.removed !== true)
            .map((attendance) => ({
              refId: attendance._id,
              label: attendance.contact?.name || (attendance.protocol ? `#${attendance.protocol}` : ''),
              subtitle: [
                attendance.protocol ? `#${attendance.protocol}` : '',
                attendance.contact?.phone ?? '',
              ].filter((part) => !!part).join(' · '),
              statusKey: ATTENDANCE_STATUS_KEYS[attendance.status] ?? undefined,
            }));
        }),
        catchError(() => this.fail()),
        finalize(() => this.loading = false),
      );
  }

  // Cards: never a removed one, never a PRIVATE one — a private deal attached
  // to a conversation would leak its snapshot to every member, so even the
  // owner's own private cards stay out of the picker (the /list endpoint
  // already hides other people's).
  private loadCards(term: string): Observable<ChatContextRefOption[]> {
    // /list answers a plain array (no server paging) — the shared paginator
    // stays hidden here (`pager = null`); funnel · stage come populated.
    this.pager = null;
    return this.cardService.getWithParams('/list', term ? { name: term } : {})
      .pipe(
        map((cards: Card[]) => (cards ?? [])
          .filter((card) => card.removed !== true && card.isPrivate !== true)
          .slice(0, RESULT_LIMIT)
          .map((card) => ({
            refId: card._id,
            label: card.name ?? '',
            subtitle: [card.funnel?.name, card.stage?.name].filter((part) => !!part).join(' · '),
          }))),
        catchError(() => this.fail()),
        finalize(() => this.loading = false),
      );
  }

  private loadContacts(term: string): Observable<ChatContextRefOption[]> {
    return this.contactService.getAll(this.page, RESULT_LIMIT, term, {})
      .pipe(
        map((pager: Pager<Contact>) => {
          this.pager = pager ?? null;
          return (pager?.list ?? []).map((contact) => ({
            refId: contact._id,
            label: contact.name ?? '',
            subtitle: contact.phone ?? '',
          }));
        }),
        catchError(() => this.fail()),
        finalize(() => this.loading = false),
      );
  }

  /** Any load failure degrades to the error state — never breaks the dialog. */
  private fail(): Observable<ChatContextRefOption[]> {
    this.hasError = true;
    this.pager = null;
    return of([]);
  }
}
